import { useEffect, useRef, useState } from 'react';
import { type FetchedFile, fetchFile, saveFile } from './api.js';
import type { ModelFacts, ModelFormat, ModelStage } from './model-stage.js';
import { Modal } from './ui.js';

/**
 * A file shown in the page rather than saved: a picture in a lightbox, a model in a 3D viewer
 * (the figures review, docs/figure-plinths-plan.md §5.2).
 *
 * An operator reviewing a set looks at a dozen files and keeps none of them, so opening one is the
 * common case and saving it the rare one: Download stays, inside the preview. The bytes are fetched
 * with the session the way `download` fetches them, since every file here is behind it, and handed
 * to the loader as bytes, never as a URL. The same bytes are what Download saves.
 *
 * The 3D half is `model-stage.ts`, imported only when a model is opened, so three.js is a chunk of
 * its own that a console visit without a model never fetches.
 */

export type PreviewKind = 'image' | ModelFormat;

export interface PreviewFile {
  /** Where this Worker serves it, behind the session. */
  path: string;
  /** What the preview's head calls it: "Pip, snap-on 3MF". */
  label: string;
  kind: PreviewKind;
  /** Draw the model in plain grey rather than its own materials: a print file or the unpainted model. */
  plain?: boolean;
}

/** What a file is, from its name: a picture, one of the three model formats, or neither. */
export function previewKindOf(name: string): PreviewKind | null {
  const extension = name.split('.').pop()?.toLowerCase();
  if (extension === 'png' || extension === 'jpg' || extension === 'jpeg' || extension === 'webp') return 'image';
  if (extension === 'glb' || extension === 'stl' || extension === '3mf') return extension;
  return null;
}

const EXTENSIONS: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

/**
 * The name Download saves under: the Worker's, or for a file it serves without one (a set's photo)
 * one made from the label and the type.
 */
function saveName(file: FetchedFile, label: string): string {
  if (file.name !== 'download') return file.name;
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'file';
  const extension = EXTENSIONS[file.type];
  return extension ? `${slug}.${extension}` : slug;
}

export function FilePreview({ file, onClose }: { file: PreviewFile; onClose: () => void }) {
  const [fetched, setFetched] = useState<FetchedFile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchFile(file.path).then(
      (next) => live && setFetched(next),
      (failure) => live && setError(String(failure)),
    );
    return () => {
      live = false;
    };
  }, [file.path]);

  return (
    <Modal
      title={file.label}
      onClose={onClose}
      actions={
        <button disabled={!fetched} onClick={() => fetched && saveFile({ ...fetched, name: saveName(fetched, file.label) })}>
          Download
        </button>
      }
    >
      {error ? (
        <p className="error">{error}</p>
      ) : !fetched ? (
        <div className="preview-stage">
          <span className="preview-wait muted small">Loading…</span>
        </div>
      ) : file.kind === 'image' ? (
        <ImageStage blob={fetched.blob} alt={file.label} />
      ) : (
        <ModelView blob={fetched.blob} format={file.kind} plain={file.plain === true} />
      )}
    </Modal>
  );
}

function ImageStage({ blob, alt }: { blob: Blob; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return <div className="preview-stage preview-image">{url ? <img src={url} alt={alt} /> : null}</div>;
}

function ModelView({ blob, format, plain }: { blob: Blob; format: ModelFormat; plain: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<ModelStage | null>(null);
  const [facts, setFacts] = useState<ModelFacts | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const abort = new AbortController();
    void (async () => {
      try {
        const [{ mountModel }, bytes] = await Promise.all([import('./model-stage.js'), blob.arrayBuffer()]);
        if (abort.signal.aborted) return;
        const mounted = await mountModel(element, bytes, format, abort.signal, { plain });
        if (abort.signal.aborted) {
          mounted.dispose();
          return;
        }
        stage.current = mounted;
        setFacts(mounted.facts);
      } catch (failure) {
        if (abort.signal.aborted || (failure instanceof DOMException && failure.name === 'AbortError')) return;
        setError(failure instanceof Error ? failure.message : String(failure));
      }
    })();
    return () => {
      abort.abort();
      stage.current?.dispose();
      stage.current = null;
    };
  }, [blob, format, plain]);

  return (
    <>
      <div ref={host} className="preview-stage preview-model">
        {!facts && !error ? <span className="preview-wait muted small">Drawing the model…</span> : null}
        {error ? <p className="error preview-wait">{error}</p> : null}
      </div>
      <div className="row preview-bar">
        <span className="muted small">
          {facts ? (
            <>
              {facts.sizeMm ? `${facts.sizeMm.join(' × ')} mm · ` : null}
              {facts.triangles.toLocaleString()} triangles
            </>
          ) : null}
        </span>
        <span className="grow" />
        <span className="muted small">Drag to turn, scroll to zoom, double-click to reset.</span>
        <button className="link small" disabled={!facts} onClick={() => stage.current?.reset()}>
          Reset view
        </button>
      </div>
    </>
  );
}
